import { useState, useEffect, useRef } from 'react';
import UserCard from './UserCard';
import './styles.css';

export default function App() {
  const [users, setUsers]     = useState([]);
  const [search, setSearch]   = useState('');
  const [sortBy, setSortBy]   = useState('name');
  const [company, setCompany] = useState('All Companies');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetch('https://jsonplaceholder.typicode.com/users')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      })
      .then(data => { setUsers(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => {
    const onKeyDown = event => {
      const isTypingInInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);

      if (event.key === 'Escape') {
        if (!isTypingInInput) {
          event.preventDefault();
          searchInputRef.current?.focus();
          return;
        }

        if (document.activeElement === searchInputRef.current) {
          setSearch('');
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const normalizedSearch = search.trim().toLowerCase();
  const companies = ['All Companies', ...new Set(users.map(u => u.company.name))];

  const filtered = users
    .filter(u => {
      const searchableText = [
        u.name,
        u.username,
        u.email,
        u.address.city,
        u.address.street,
        u.address.suite,
        u.address.zipcode,
        u.company.name,
        u.company.catchPhrase,
        u.company.bs,
      ].join(' ').toLowerCase();

      const matchesName = searchableText.includes(normalizedSearch);
      const matchesCompany = company === 'All Companies' || u.company.name === company;
      return matchesName && matchesCompany;
    })
    .sort((a, b) => {
      if (sortBy === 'company') {
        return a.company.name.localeCompare(b.company.name);
      }
      if (sortBy === 'city') {
        return a.address.city.localeCompare(b.address.city);
      }
      return a.name.localeCompare(b.name);
    });

  const hasSearch = normalizedSearch.length > 0;
  const hasCompanyFilter = company !== 'All Companies';
  const hasCustomSort = sortBy !== 'name';

  const resetAll = () => {
    setSearch('');
    setSortBy('name');
    setCompany('All Companies');
  };

  return (
    <div className="app-shell">
      <div className="bg-orb orb-one" />
      <div className="bg-orb orb-two" />
      <div className="bg-orb orb-three" />

      <main className="app">
        <section className="hero">
          <div className="hero-topline">
            <p className="eyebrow">Team Navigator</p>
            <span className="live-badge">Live Directory</span>
          </div>
          <h1>User Directory</h1>
          <p className="subtitle">
            Find teammates by name, company, and location through an interface designed for daily use.
          </p>
          <p className="hero-note">Live dataset connected to JSONPlaceholder users API.</p>

          <div className="meta-strip">
            <div className="meta-pill">
              <span className="meta-label">Total Users</span>
              <strong>{users.length}</strong>
            </div>
            <div className="meta-pill">
              <span className="meta-label">Results</span>
              <strong>{loading ? '...' : filtered.length}</strong>
            </div>
            <div className="meta-pill">
              <span className="meta-label">Companies</span>
              <strong>{new Set(users.map(u => u.company.name)).size}</strong>
            </div>
          </div>
        </section>

        <section className="control-panel">
          <div className="search-wrap">
            <span className="search-icon" aria-hidden="true">SEARCH</span>
            <input
              ref={searchInputRef}
              className="search"
              type="text"
              aria-label="Search users by name"
              placeholder="Search by name..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <p className="search-hint">Press Esc to focus search</p>
          </div>

          <div className="toolbar">
            <label className="sort-field">
              <span>Sort by</span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="name">Name</option>
                <option value="company">Company</option>
                <option value="city">City</option>
              </select>
            </label>

            <button
              type="button"
              className="reset-btn"
              onClick={resetAll}
              disabled={!hasSearch && !hasCompanyFilter && sortBy === 'name'}
            >
              Reset
            </button>
          </div>

          <div className="active-filters" aria-label="Current filters and sorting">
            {!hasSearch && !hasCompanyFilter && !hasCustomSort && (
              <span className="active-chip neutral">Showing all users</span>
            )}
            {hasSearch && <span className="active-chip">Query: {search.trim()}</span>}
            {hasCompanyFilter && <span className="active-chip">Company: {company}</span>}
            {hasCustomSort && <span className="active-chip">Sorted by: {sortBy}</span>}
          </div>

          <div className="company-filters" role="group" aria-label="Filter by company">
            {companies.map(companyName => (
              <button
                key={companyName}
                type="button"
                className={`filter-chip ${company === companyName ? 'active' : ''}`}
                aria-pressed={company === companyName}
                onClick={() => setCompany(companyName)}
              >
                {companyName}
              </button>
            ))}
          </div>
        </section>

        {loading && <p className="status">Loading users...</p>}
        {error && <p className="status error">Error: {error}</p>}
        {!loading && !error && filtered.length === 0 && (
          <div className="status status-empty">
            <p>No users found for the current filters.</p>
            <button type="button" className="status-action" onClick={resetAll}>Clear filters</button>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <p className="result-count" role="status" aria-live="polite">
            Showing {filtered.length} {filtered.length === 1 ? 'profile' : 'profiles'}
            {hasSearch ? ` for "${search.trim()}"` : ''}
            {hasCompanyFilter ? ` in ${company}` : ''}
          </p>
        )}

        <div className="grid">
          {filtered.map((user, index) => (
            <UserCard key={user.id} user={user} index={index} />
          ))}
        </div>
      </main>
      <footer className="footer-note">Built for speed, clarity, and better team discovery.</footer>
    </div>
  );
}